import type { Contract, ContractState, Customer, CustomerStatus, Deal, DealStage, MyHana } from './types';

/** Bỏ dấu + hạ chữ thường, để tìm "hà nội" ra "TP Hà Nội". */
export const norm = (s: string): string =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase();

export const STATUS_LABEL: Record<CustomerStatus, string> = { ap: 'Đã duyệt', wait: 'Chờ duyệt', inact: 'Ngưng dùng', bl: 'Blacklist' };
export const CONTRACT_LABEL: Record<ContractState, string> = { sign: 'HĐ đã ký', unsigned: 'HĐ chưa ký', exp: 'HĐ sắp hết hạn', none: 'Chưa có HĐ' };
export const MY_LABEL: Record<MyHana, string> = { ok1: 'Đã có · 1 user', ok2: 'Đã có · 2 user', ok3: 'Đã có · 3 user', ok4: 'Đã có · 4 user', wait: 'Chờ BOD duyệt', no: 'Chưa có' };

/** Nhóm myHANA ở bộ lọc: bỏ phần "· n user". */
export const myGroup = (my: MyHana): string => MY_LABEL[my].split(' · ')[0]!;

/** Tìm nhanh theo tên + MST (không phân biệt dấu / hoa thường). */
export function searchCustomers(rows: readonly Customer[], q: string): Customer[] {
  const n = norm(q.trim());
  return n ? rows.filter((r) => norm(`${r.id} ${r.mst}`).includes(n)) : [...rows];
}

export const hasOverdue = (c: Customer): boolean => c.credit.overdue > 0;
export const overLimit = (c: Customer): boolean => c.credit.used > c.credit.limit;

const jobBand = (c: Customer): string => (c.jobs >= 20 ? 'Từ 20 job' : c.jobs >= 10 ? '10–19 job' : 'Dưới 10 job');
const missing = (c: Customer): string[] =>
  [c.contactCount === 0 ? 'Thiếu liên hệ' : '', c.province === '—' ? 'Thiếu tỉnh' : '', c.bankCount === 0 ? 'Thiếu TK ngân hàng' : ''].filter(Boolean);

/** Giá trị mỗi khoá lọc của board Mint-Customers; một khách khớp khi có ít nhất một giá trị nằm trong lựa chọn. */
export const FILTER_KEYS = {
  sale: (c: Customer) => [c.sale],
  bkg: (c: Customer) => [c.bkg],
  my: (c: Customer) => [myGroup(c.my)],
  debt: (c: Customer) => [hasOverdue(c) ? 'Có quá hạn' : 'Không quá hạn', ...(overLimit(c) ? ['Vượt hạn mức'] : [])],
  status: (c: Customer) => [STATUS_LABEL[c.status]],
  contract: (c: Customer) => [CONTRACT_LABEL[c.contract]],
  jobs: (c: Customer) => [jobBand(c)],
  region: (c: Customer) => [c.region],
  province: (c: Customer) => [c.province],
  tier: (c: Customer) => [`Hạng ${c.tier}`],
  segment: (c: Customer) => [c.segment],
  kind: (c: Customer) => [c.kind],
  missing,
} as const;
export type FilterKey = keyof typeof FILTER_KEYS;
export interface CustomerFilter { key: FilterKey; values: string[] }

/** Các bộ lọc ghép bằng AND; trong một bộ lọc ghép bằng OR. */
export function filterCustomers(rows: readonly Customer[], filters: readonly CustomerFilter[]): Customer[] {
  return rows.filter((c) => filters.every((f) => FILTER_KEYS[f.key](c).some((v) => f.values.includes(v))));
}

/** Số khách theo trạng thái hồ sơ (thẻ đầu trang). */
export function statusCounts(rows: readonly Customer[]): Record<CustomerStatus, number> {
  const out: Record<CustomerStatus, number> = { ap: 0, wait: 0, inact: 0, bl: 0 };
  for (const c of rows) out[c.status]++;
  return out;
}

// ----- Cơ hội -----

export const STAGE_ORDER: readonly DealStage[] = ['New', 'Pricing', 'Quoted', 'Won', 'Lost'];
export const STAGE_LABEL: Record<DealStage, string> = { New: 'Mới', Pricing: 'Chờ giá', Quoted: 'Đã báo', Won: 'Won', Lost: 'Lost' };

export const isOpenDeal = (d: Deal): boolean => d.stage !== 'Won' && d.stage !== 'Lost';
/** Quá hạn follow: còn mở và hẹn ở quá khứ. */
export const isOverdueDeal = (d: Deal): boolean => isOpenDeal(d) && d.followDays !== null && d.followDays < 0;

export function dealsByStage(rows: readonly Deal[]): Record<DealStage, Deal[]> {
  const out: Record<DealStage, Deal[]> = { New: [], Pricing: [], Quoted: [], Won: [], Lost: [] };
  for (const d of rows) out[d.stage].push(d);
  return out;
}

/** Tỷ lệ thắng trong các phiếu đã đóng (0..1); chưa có phiếu đóng → 0. */
export function winRate(rows: readonly Deal[]): number {
  const won = rows.filter((d) => d.stage === 'Won').length;
  const closed = won + rows.filter((d) => d.stage === 'Lost').length;
  return closed ? won / closed : 0;
}

// ----- Hợp đồng -----

/** Hợp đồng sắp hết hạn: còn hiệu lực nhưng ≤ 60 ngày. */
export const EXPIRING_DAYS = 60;
export const isExpired = (c: Contract): boolean => c.daysLeft < 0;
export const isExpiring = (c: Contract): boolean => c.daysLeft >= 0 && c.daysLeft <= EXPIRING_DAYS;

/** Tình trạng hợp đồng hiệu lực của một khách: ký + chưa hết hạn → sign / sắp hết hạn → exp / có HĐ nhưng chưa ký → unsigned / còn lại none. */
export function contractStateOf(customer: string, rows: readonly Contract[]): ContractState {
  const mine = rows.filter((c) => c.customer === customer && !isExpired(c));
  if (mine.some((c) => c.signed && !isExpiring(c))) return 'sign';
  if (mine.some((c) => c.signed)) return 'exp';
  return mine.length ? 'unsigned' : 'none';
}

/** Hợp đồng còn thiếu thông tin hoặc chưa ký / chưa duyệt — việc cần làm của Sale. */
export const needsAction = (c: Contract): boolean => !isExpired(c) && (!c.signed || !c.approved || c.missing.length > 0);
